import {
  showForgeSuccessToastNow,
  showForgeErrorToastNow,
} from 'common/components/ToastNotification/Toastmaster';
import { fetchJsonWithDefaultHeaders } from 'common/http';
import { I18n } from 'common/i18n';
import { useEffect, useRef, useState } from 'react';
import { useDerivedReport } from '../DerivedReportContext';
import { useSelector } from 'react-redux';
import { selectSerializedDerivedReport } from 'store/selectors/StorySelectors/StorySerialize';

/**
 * Creates a function for you to call to update a derived report. It also provides you an
 * `inProgress` state that you can use to display when the update call is in flight.
 *
 * When the update is done, it'll show success and failure toasts.
 *
 * @returns updateDerivedReport: a function to call to save the current story json as the
 * new current derived view. It'll set a toast when this action returns.
 *
 * inProgress: true when the update derived report call is happening and false otherwise.
 */
export function useUpdateDerivedReport() {
  const [inProgress, setInProgress] = useState(false);
  const { currentStory } = useDerivedReport();
  const isMounted = useRef(true);
  const derivedReport = useSelector(selectSerializedDerivedReport(currentStory.id));

  /**
   * This provides clean up for this hook. We don't want to be setting any state
   * if the component this is in decides to unmount.
   */
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const updateDerivedReport = async () => {
    setInProgress(true);

    try {
      await fetchJsonWithDefaultHeaders(`/stories/api/v1/stories/${currentStory.id}/child_reports`, {
        method: 'PUT',
        body: JSON.stringify(derivedReport),
      });

      showForgeSuccessToastNow(
        I18n.t('derived_story.update_success_toast', { reportName: currentStory.name }),
      );

      if (isMounted.current) {
        setInProgress(false);
      }
    } catch (error) {
      console.error('Error creating derived story: ', error);

      showForgeErrorToastNow(I18n.t('derived_story.update_error_toast'));

      if (isMounted.current) {
        setInProgress(false);
      }
    }
  };

  return { updateDerivedReport, inProgress };
}
